<template>
  <modal :value="showModal" title="On-demand Import" @hide="onCancel" @input="updateShowModal" :header-close-btn="true">
    <!-- Status alerts -->
    <div v-if="importStatus.loading" class="alert alert-info">
      <p><i class="fa fa-spinner fa-spin"></i> Initiating on-demand import...</p>
    </div>
    <div v-if="importStatus.success" class="alert alert-success">
      <p><i class="fa fa-check-circle"></i> On-demand import initiated successfully!</p>
      <p>Import ID: {{ importStatus.importUuid }}</p>
    </div>
    <div v-if="importStatus.error" class="alert alert-danger">
      <p><i class="fa fa-exclamation-circle"></i> Error initiating on-demand import:</p>
      <p>{{ importStatus.errorMessage }}</p>
    </div>
    
    <div v-if="!importStatus.loading && !importStatus.success && !importStatus.error">
      <div class="form-group">
        <div class="checkbox">
          <label>
            <input type="checkbox" v-model="options.autocommit">
            <strong>Auto-commit Import</strong>
          </label>
          <p class="help-block">
            When enabled, imported user changes will automatically be saved.
          </p>
        </div>
      </div>
      
      <div class="form-group">
        <div class="checkbox">
          <label>
            <input type="checkbox" v-model="options.deactivate_missing">
            <strong>Deactivate Missing Users</strong>
          </label>
          <p class="help-block">
            When enabled, users not present in the import will be automatically deactivated.
          </p>
        </div>
      </div>

      <div class="form-group">
        <div class="checkbox">
          <label>
            <input type="checkbox" v-model="options.autoupdate_titles">
            <strong>Auto-update titles with provider data</strong>
          </label>
          <p class="help-block">
            When enabled, users will receive titles from the provider. When disabled, users will keep local titles.
          </p>
        </div>
      </div>
    </div>
    
    <div slot="footer">
      <button type="button" class="btn btn-default" @click="onCancel">Cancel</button>
      <button 
        v-if="!importStatus.loading && !importStatus.success && !importStatus.error"
        type="button" 
        class="btn btn-primary" 
        @click="startImport" 
        :disabled="importStatus.loading"
      >
        <i class="fa fa-play"></i>
        Begin Import
      </button>
      <button 
        v-if="importStatus.success || importStatus.error"
        type="button" 
        class="btn btn-primary" 
        @click="() => { resetStatus(); updateShowModal(false) }"
      >
        Close
      </button>
    </div>
  </modal>
</template>

<script>
import { Modal } from 'uiv'

export default {
  name: 'ImportModal',
  components: {
    Modal
  },
  props: {
    externalOpen: {
      type: Boolean,
      default: false
    },
    domainUuid: {
      type: String,
      required: true
    },
    config: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      options: {
        autocommit: true,
        deactivate_missing: false,
        autoupdate_titles: true,
      },
      importStatus: {
        loading: false,
        success: false,
        error: false,
        importUuid: null,
        errorMessage: null
      }
    }
  },
  computed: {
    showModal() {
      return this.externalOpen
    }
  },
  watch: {
    externalOpen(newVal) {
      if (newVal) {
        // Reset options and status when modal is opened
        this.resetOptions()
        this.resetStatus()
      }
    },
    config: {
      immediate: true,
      handler() {
        // Update options when config changes
        this.resetOptions()
      }
    }
  },
  methods: {
    resetOptions() {
      // Get values from config, with appropriate defaults
      const autocommit = this.config?.lambda_input?.import_options?.autocommit === true;
      const deactivate_missing = this.config?.lambda_input?.import_options?.deactivate_missing === true;
      const autoupdate_titles = this.config?.lambda_input?.import_options?.autoupdate_titles === true;
      this.options = { autocommit, deactivate_missing, autoupdate_titles };
    },
    onCancel() {
      this.$emit('update:externalOpen', false)
      this.$emit('cancel')
    },
    updateShowModal(value) {
      this.$emit('update:externalOpen', value)
      if (!value) {
        this.resetStatus()
        this.resetOptions()
        this.$emit('cancel')
      }
    },
    resetStatus() {
      this.importStatus = {
        loading: false,
        success: false,
        error: false,
        importUuid: null,
        errorMessage: null
      }
    },
    startImport() {
      this.resetStatus()
      this.importStatus.loading = true
      this.$angularWorld.adminApi.initiateAutoprovisioning(this.domainUuid, this.options)
        .then(response => {
          console.log('Autoprovisioning initiated successfully:', response)
          this.importStatus.loading = false
          this.importStatus.success = true
          this.importStatus.importUuid = response.body || 'N/A'

          let importUrl = `${this.$angularWorld.config.root}#!/import_wizard/${this.domainUuid}/${response.body}/confirm`
          
          this.$emit('success', importUrl)
        })
        .catch(error => {
          console.error('Error initiating on-demand import:', error)
          this.importStatus.loading = false
          this.importStatus.error = true
          this.importStatus.errorMessage = typeof error === 'string' ? error : (error?.data?.message || 'Failed to initiate on-demand import')
          this.$emit('error', error)
        })
    }
  }
}
</script>
